import { apiRequest } from './api';
import {
  decodeContestDetail,
  decodeContestList,
  decodeContestProblem,
  decodeContestSubmissions,
  decodeLeaderboard,
  decodeSubmissionStatus,
} from './contestDecoders';
import { useServerClock } from '../stores/serverClock';

async function call(path, options = {}, decoder = null) {
  const response = await apiRequest(path, options, decoder);
  if (response.success && typeof response.data?.serverNow === 'string') {
    useServerClock.getState().sync(response.data.serverNow);
  }
  return response;
}

export const contestApi = {
  list: () => call('/contests', {}, decodeContestList),
  detail: (contestId) => call(`/contests/${contestId}`, {}, decodeContestDetail),
  register: (contestId) => call(`/contests/${contestId}/register`, { method: 'POST' }),
  unregister: (contestId) => call(`/contests/${contestId}/register`, { method: 'DELETE' }),
  problem: (contestId, problemId) =>
    call(`/contests/${contestId}/problems/${problemId}`, {}, decodeContestProblem),
  run: (contestId, problemId, body) =>
    call(`/contests/${contestId}/problems/${problemId}/run`, { method: 'POST', body }),
  submit: (contestId, problemId, body) =>
    call(`/contests/${contestId}/problems/${problemId}/submit`, { method: 'POST', body }),
  submissions: (contestId, problemId) =>
    call(`/contests/${contestId}/submissions?problemId=${problemId}`, {}, decodeContestSubmissions),
  submissionStatus: (submissionId) =>
    apiRequest(`/contests/submissions/${submissionId}`, {}, decodeSubmissionStatus),
  runStatus: (token) => apiRequest(`/platform/run/${token}`),
  leaderboard: (contestId, page = 1) =>
    call(`/contests/${contestId}/leaderboard?page=${page}`, {}, decodeLeaderboard),
};

const POLL_DELAYS_MS = [600, 800, 1000, 1500, 2000, 3000];
const POLL_TIMEOUT_MS = 120_000;

export function pollUntil(fetcher, isDone, { signal } = {}) {
  return new Promise((resolve, reject) => {
    const startedAt = Date.now();
    let attempt = 0;
    let timer = null;

    const stop = () => clearTimeout(timer);
    signal?.addEventListener('abort', () => {
      stop();
      reject(new DOMException('Polling aborted', 'AbortError'));
    });

    const tick = async () => {
      if (signal?.aborted) return;
      const response = await fetcher();
      if (signal?.aborted) return;

      if (response.success && isDone(response.data)) {
        resolve(response.data);
        return;
      }
      if (!response.success && response.status && response.status < 500 && response.status !== 429) {
        reject(new Error(response.error || 'Request failed'));
        return;
      }
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        reject(new Error('Judging is taking longer than usual. Check your submissions in a moment.'));
        return;
      }
      const delay = POLL_DELAYS_MS[Math.min(attempt, POLL_DELAYS_MS.length - 1)];
      attempt += 1;
      timer = setTimeout(tick, delay);
    };

    timer = setTimeout(tick, POLL_DELAYS_MS[0]);
  });
}
